import styled from '@emotion/styled';
import qs from 'qs';
import { useEffect, useMemo, useRef, useState } from 'react';

import Text from './utilities/Text';
import mdmsApi from '../../../mdms_api';
import { profileURL } from '../../FindAContractor/profileURL';
import { Screens } from '../../PageBuilder/editing/Viewport';
import { interpolateString } from '../../PageBuilder/helpers/interpolate';
import { openComponentModal } from '../../PageBuilder/helpers/modal';
import { SchemaForm } from '../../forms/InnerSchemaForm';
import { connectLocation } from '../../location/LocationConnectors';
import Cta from '../oc-cta';
import { H2, H4, H5 } from '../oc-h';
import Icon from '../oc-icon';
import Image from '../oc-image';
import Picture from '../oc-picture';

export const SOURCE_MODES = {
  nearby: 'nearby',
  specific: 'specific',
};

export const LOCATION_MODES = {
  visitor: 'visitor',
  postal: 'postal',
};

export const CONTRACTOR_LEVELS = {
  platinumPreferred: 'platinumPreferred',
  preferred: 'preferred',
};

const CONTRACTOR_LEVEL_TIERS = {
  [CONTRACTOR_LEVELS.platinumPreferred]: 3,
  [CONTRACTOR_LEVELS.preferred]: 2,
};

export const BADGE_FILTERS = {
  financing: ['FIN'],
  awardWinner: [
    'PEPRDCT',
    'PESVC',
    'PPCOMM',
    'PPSAFE',
    'PPSUSTAIN',
    'PPINNOV',
    'PTPGRTH',
    'PTPMVP',
    'PTPWARR',
    'PTPVOL',
    'CEEBB',
    'CEECS',
    'CEEI',
    'CEEPY',
    'CEEQP',
  ],
  recycling: ['RECYCLER'],
  solar: ['SOLAR'],
  topOfHouse: ['TOTH'],
  sureNail: ['SN'],
  tprs: ['TPRS'],
};

const DEFAULT_LOGO =
  'https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_65/v1589224258/roofing/contractors/house-generic.png';
const EMPTY_SELECTIONS = [];
const EMPTY_ADDRESS = {};

const ModuleWrapper = styled.div`
  align-items: flex-start;
  color: #000;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  > h2,
  > h5 {
    margin: 0;
  }
`;

const ContractorList = styled.div`
  display: grid;
  gap: 24px 32px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;

  @media (max-width: ${Screens.Tablet}px) {
    grid-template-columns: 1fr;
  }
`;

const ContractorCard = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 72px minmax(0, 1fr);
`;

const Logo = styled.div`
  align-items: flex-start;
  display: flex;
  height: 72px;
  justify-content: center;
  width: 72px;

  picture,
  img {
    display: block;
    height: 100%;
    max-height: 72px;
    max-width: 72px;
    object-fit: contain;
    width: 100%;
  }
`;

const ContractorDetails = styled.div`
  min-width: 0;

  h4 a {
    color: inherit;
    text-decoration: none;
  }
`;

const ContractorMeta = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;

  > img {
    height: auto;
    max-width: 100px;
  }
`;

const RatingSummary = styled.span`
  align-items: center;
  color: #333;
  display: inline-flex;
  gap: 4px;
  line-height: 21px;

  .single-star {
    color: #fbaa19;
    display: inline-flex;
    font-size: 19px;
    line-height: 1;
  }

  .rating-number {
    color: #000;
    font-weight: 700;
  }

  .review-count {
    font-size: 12px;
  }
`;

const QuoteAction = styled.div`
  display: flex;
  flex-wrap: wrap;
  line-height: 22px;
`;

const QuoteLink = styled.a`
  align-items: center;
  color: inherit;
  display: inline-flex;
  gap: 10px;
  text-decoration: none !important;

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none !important;
  }
`;

const QuoteChevron = styled.span`
  display: inline-flex;

  &::after {
    content: '\\e608';
    font-family: 'icomoon';
  }
`;

const Loading = styled.div`
  min-height: 72px;
`;

const getCountryCode = localeCode => localeCode?.split('-')[1]?.toUpperCase() || 'US';

export const extractMembershipNumber = contractorProfileUrl => {
  if (!contractorProfileUrl?.trim()) return null;

  try {
    const url = new URL(contractorProfileUrl.trim(), 'https://www.owenscorning.com');
    const match = url.pathname.match(/\/roofing\/contractors\/contractor-profile\/([^/]+)\/?$/i);
    return match ? decodeURIComponent(match[1]) : null;
  } catch (_error) {
    return null;
  }
};

export const filterNearbyContractors = (
  contractors,
  { badges = EMPTY_SELECTIONS, contractorLevels = EMPTY_SELECTIONS, showOnlyWithLogos = true } = {}
) =>
  (contractors || []).filter(contractor => {
    const tierLevel = Number(contractor.tier_level);
    const contractorBadges = contractor.badges || [];
    const matchesTier =
      !contractorLevels.length || contractorLevels.some(level => CONTRACTOR_LEVEL_TIERS[level] === tierLevel);
    const matchesLogo = !showOnlyWithLogos || Boolean(contractor.logo);
    const matchesBadges = badges.every(badge => {
      const badgeCodes = BADGE_FILTERS[badge] || [];
      return badgeCodes.some(code => contractorBadges.includes(code));
    });

    return matchesTier && matchesLogo && matchesBadges;
  });

const highestReviewedContractor = contractors =>
  contractors.reduce((selected, contractor) => {
    if (!selected) return contractor;
    return Number(contractor.gq_review_count || 0) > Number(selected.gq_review_count || 0) ? contractor : selected;
  }, null);

export const orderSpecificContractors = (profileUrls, contractors) => {
  const contractorsByMembership = (contractors || []).reduce((grouped, contractor) => {
    const membershipNumber = String(contractor.membership_number);
    return {
      ...grouped,
      [membershipNumber]: [...(grouped[membershipNumber] || []), contractor],
    };
  }, {});

  return (profileUrls || [])
    .filter(profileUrl => profileUrl?.trim())
    .map(profileUrl => {
      const membershipNumber = extractMembershipNumber(profileUrl);
      return membershipNumber ? highestReviewedContractor(contractorsByMembership[membershipNumber] || []) : null;
    })
    .filter(Boolean);
};

const requestJson = async url => {
  const response = await mdmsApi.get(url);
  if (response.status === 204) return [];
  if (response.status !== 200) throw new Error(`Contractor request failed with status ${response.status}`);
  const json = await response.json();
  return json.data || json;
};

const resolvePostalCode = async (postalCode, localeCode) => {
  if (!postalCode?.trim()) return null;
  const query = qs.stringify({ zip: postalCode.trim(), c_code: localeCode });
  const response = await mdmsApi.get(`/api/v1/validzip?${query}`);
  if (response.status !== 200) return null;
  const address = await response.json();
  return address.valid ? address : null;
};

const nearbyContractorUrl = ({ address, radius }) => {
  const query = qs.stringify(
    {
      lat: address.latitude,
      lng: address.longitude,
      per_page: 10000,
      radius,
      sort: 'default',
      units: address.country_code === 'US' ? 'mi' : 'km',
      c_code: address.country_code,
    },
    { arrayFormat: 'brackets' }
  );
  return `/api/v3/locations/roofing-contractors?${query}`;
};

const specificContractorUrl = (profileUrls, localeCode) => {
  const membershipNumbers = profileUrls.map(extractMembershipNumber).filter(Boolean);
  if (!membershipNumbers.length) return null;
  const query = qs.stringify({
    membership_number: [...new Set(membershipNumbers)].join(','),
    c_code: getCountryCode(localeCode),
    per_page: 100,
  });
  return `/api/v3/locations/roofing-contractors?${query}`;
};

const RequestAQuoteModal = ({ code, contractor, zip }) => {
  const params = contractor
    ? {
        initialWidth: 0,
        parentTitle: typeof document !== 'undefined' ? document.title : '',
        parentUrl: typeof document !== 'undefined' ? document.location.href : '',
        zipcode: zip,
        contractor: contractor.membership_number,
        contractor_mas_dealer_code: contractor.membership_number,
        source: 'custom-fac',
        'meta[data-track-contractor-id]': contractor.membership_number,
        'meta[data-track-contractor]': contractor.name,
        lang: code,
        contractor_store_name: contractor.name,
      }
    : {};

  return (
    <SchemaForm
      uid={`www.owenscorning.com/${(code || 'en-US').toLowerCase()}/roofing/request-a-quote-form`}
      search={`?${qs.stringify(params)}`}
    />
  );
};

const Contractor = ({ contractor, locale, t, zip }) => {
  const contractorProfile = profileURL(contractor.membership_number, locale.code);
  const rating = Number(contractor.gq_review_stars || 0).toFixed(1);
  const reviewCount = Number(contractor.gq_review_count || 0).toLocaleString(locale.code);
  const reviewCountCopy = t('contractor_profile.review_count', { count: reviewCount });

  return (
    <ContractorCard data-testid="fac-contractor-card">
      <Logo>
        <Picture source={contractor.logo || DEFAULT_LOGO} alt={contractor.name} transform={{ width: 144 }} />
      </Logo>
      <ContractorDetails>
        <H4>
          <a href={contractorProfile}>
            <Text content={contractor.name} />
          </a>
        </H4>
        <ContractorMeta>
          {Number(contractor.tier_level) === 3 && (
            <Image
              src={t('contractor_profile.platinum_icon')}
              alt={t('contractor_profile.platinum_title')}
              transform={{ width: 100 }}
            />
          )}
          {contractor.gq_review_count > 0 && (
            <RatingSummary role="img" aria-label={`${t('contractors.star_rating')}: ${rating}. ${reviewCountCopy}`}>
              <span className="single-star" aria-hidden="true">
                ★
              </span>
              <span className="rating-number" aria-hidden="true">
                {rating}
              </span>
              <span className="review-count" aria-hidden="true">
                {reviewCountCopy}
              </span>
            </RatingSummary>
          )}
        </ContractorMeta>
        <QuoteAction>
          <QuoteLink
            href="#request-a-quote"
            onClick={event => {
              event.preventDefault();
              openComponentModal(<RequestAQuoteModal code={locale.code} zip={zip} contractor={contractor} />);
            }}
            data-track="form-start"
            data-track-professional="contractor"
            data-track-form-name="request-a-quote"
            data-track-contractor-id={contractor.membership_number}
            data-track-contractor={contractor.name}
          >
            {t('contractor_profile.request_quote_short')}
            <QuoteChevron data-testid="fac-request-quote-chevron" aria-hidden="true" />
          </QuoteLink>
        </QuoteAction>
      </ContractorDetails>
    </ContractorCard>
  );
};

const ContractorResults = ({ contractors, loading, locale, notFoundCopy, t, zip }) => {
  if (loading) {
    return (
      <Loading role="status" aria-live="polite">
        <Icon type="spinner" className="fa-spin fa-lg" />
        <span className="sr-only">{t('generic_locator.loading')}</span>
      </Loading>
    );
  }

  if (!contractors.length) return <div>{notFoundCopy}</div>;

  return (
    <ContractorList>
      {contractors.map(contractor => (
        <Contractor
          key={`${contractor.membership_number}-${contractor.id || 'location'}`}
          contractor={contractor}
          locale={locale}
          t={t}
          zip={zip}
        />
      ))}
    </ContractorList>
  );
};

export const FindAContractor = ({
  heading = 'Find a contractor',
  bodyCopy = 'Get your roof installed by a contractor near {city}, {region}',
  ctaLabel = 'View More Contractors',
  ctaLink = 'https://www.owenscorning.com/roofing/contractors',
  notFoundCopy = "We're sorry, we did not find any contractors in your area.",
  sourceMode = SOURCE_MODES.nearby,
  locationMode = LOCATION_MODES.visitor,
  postalCode = '',
  previewPostalCode = '90210',
  radius = 20,
  badges = EMPTY_SELECTIONS,
  contractorLevels = EMPTY_SELECTIONS,
  showOnlyWithLogos = true,
  contractorUrl1 = '',
  contractorUrl2 = '',
  contractorUrl3 = '',
  isEditor = false,
  address = EMPTY_ADDRESS,
  t,
  locale,
}) => {
  const ctaButtonRef = useRef(null);
  const [contractors, setContractors] = useState([]);
  const [displayAddress, setDisplayAddress] = useState(address);
  const [loading, setLoading] = useState(true);
  const profileUrls = useMemo(
    () => [contractorUrl1, contractorUrl2, contractorUrl3],
    [contractorUrl1, contractorUrl2, contractorUrl3]
  );

  useEffect(() => {
    let active = true;

    const loadContractors = async () => {
      setLoading(true);

      try {
        if (sourceMode === SOURCE_MODES.specific) {
          const url = specificContractorUrl(profileUrls, locale.code);
          const results = url ? await requestJson(url) : [];
          if (active) {
            setDisplayAddress(address);
            setContractors(orderSpecificContractors(profileUrls, results));
          }
          return;
        }

        let searchAddress = address;
        if (locationMode === LOCATION_MODES.postal) {
          searchAddress = await resolvePostalCode(postalCode, locale.code);
        } else if (isEditor) {
          searchAddress = await resolvePostalCode(previewPostalCode, locale.code);
        }

        if (searchAddress?.latitude == null || searchAddress?.longitude == null) {
          if (active) {
            setDisplayAddress(searchAddress || EMPTY_ADDRESS);
            setContractors([]);
          }
          return;
        }

        const results = await requestJson(nearbyContractorUrl({ address: searchAddress, radius }));
        if (active) {
          setDisplayAddress(searchAddress);
          setContractors(filterNearbyContractors(results, { badges, contractorLevels, showOnlyWithLogos }).slice(0, 3));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Unable to load customizable Find a Contractor results:', error);
        if (active) setContractors([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadContractors();
    return () => {
      active = false;
    };
  }, [
    address,
    badges,
    contractorLevels,
    isEditor,
    locale.code,
    locationMode,
    postalCode,
    previewPostalCode,
    profileUrls,
    radius,
    showOnlyWithLogos,
    sourceMode,
  ]);

  const interpolatedBodyCopy = interpolateString(bodyCopy, {
    city: displayAddress?.city,
    region: displayAddress?.region,
  });
  const bodyNeedsLocation = /{(?:city|region)}/i.test(bodyCopy);
  const showBodyCopy = !bodyNeedsLocation || Boolean(displayAddress?.city || displayAddress?.region);

  return (
    <ModuleWrapper>
      <H2 tag="h2">
        <Text content={heading} />
      </H2>
      {showBodyCopy && (
        <H5>
          <Text content={interpolatedBodyCopy} />
        </H5>
      )}
      <ContractorResults
        contractors={contractors}
        loading={loading}
        locale={locale}
        notFoundCopy={notFoundCopy}
        t={t}
        zip={displayAddress?.zip_code}
      />
      <Cta
        color="white"
        href={ctaLink}
        text={ctaLabel}
        forwardRef={ctaButtonRef}
        analytics={{ track: 'learn-more', 'track-destination': ctaLink }}
      >
        <Text content={ctaLabel} />
      </Cta>
    </ModuleWrapper>
  );
};

export default connectLocation(FindAContractor);
